import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, EyeOff, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { API } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Field } from "@/components/common";
import { Confirm, FormDialog } from "@/components/kids/kidsUi";

const ORIGIN = API.replace(/\/api$/, "");
export const src = (u) => (u ? `${ORIGIN}${u}` : null);

function TagPicker({ people, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2">
      {people.map((p) => {
        const on = value.includes(p.key);
        return <button key={p.key} aria-pressed={on} onClick={() => onChange(on ? value.filter((x) => x !== p.key) : [...value, p.key])} className={`min-h-[44px] rounded-full border px-3 text-sm font-semibold ${on ? "border-transparent bg-[rgb(var(--glow-home))] text-white" : "border-q-line text-q-muted"}`} data-testid={`tag-${p.key}`}>{p.name}</button>;
      })}
    </div>
  );
}

export function EditDialog({ hubId, people, m, onClose, onSaved }) {
  const [f, setF] = useState({ caption: m.caption, tags: m.tags.map((t) => t.key), date: m.date });
  const save = () => api.patch(`/organizations/${hubId}/memories/${m.id}`, f).then(() => { toast.success("Saved"); onSaved(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <FormDialog open onClose={onClose} title="Edit memory" testid="memory-edit-dialog">
      <div className="space-y-3">
        <Field label="Caption"><input className="q-input min-h-[44px] w-full" maxLength={200} value={f.caption} onChange={(e) => setF({ ...f, caption: e.target.value })} data-testid="memory-caption-input" /></Field>
        <Field label="Date"><input type="date" className="q-input min-h-[44px] w-full" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="memory-date-input" /></Field>
        <Field label="Who's in it"><TagPicker people={people} value={f.tags} onChange={(tags) => setF({ ...f, tags })} /></Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" onClick={save} data-testid="memory-save-btn">Save</button>
      </div>
    </FormDialog>
  );
}

export function MilestoneDialog({ hubId, people, ms, onClose, onSaved }) {
  const [f, setF] = useState({ title: ms?.title || "", emoji: ms?.emoji || "⭐", date: ms?.date || new Date().toISOString().slice(0, 10), tags: (ms?.tags || []).map((t) => t.key), note: ms?.note || "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, memory_ids: (ms?.photos || []).map((p) => p.id) };
      const { data } = ms ? await api.put(`/organizations/${hubId}/milestones/${ms.id}`, body) : await api.post(`/organizations/${hubId}/milestones`, body);
      if (files.length) {
        const fd = new FormData(); files.forEach((x) => fd.append("files", x));
        await api.post(`/organizations/${hubId}/milestones/${data.id}/photos`, fd);
      }
      toast.success("Milestone saved"); onSaved();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <FormDialog open onClose={onClose} title={ms ? "Edit milestone" : "Add a milestone"} testid="milestone-dialog">
      <div className="space-y-3">
        <div className="grid grid-cols-[72px_1fr] gap-2">
          <Field label="Emoji"><input className="q-input min-h-[44px] w-full text-center text-xl" maxLength={8} value={f.emoji} onChange={(e) => setF({ ...f, emoji: e.target.value })} data-testid="milestone-emoji-input" /></Field>
          <Field label="Title"><input className="q-input min-h-[44px] w-full" maxLength={80} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="First lost tooth" data-testid="milestone-title-input" /></Field>
        </div>
        <Field label="Date"><input type="date" className="q-input min-h-[44px] w-full" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="milestone-date-input" /></Field>
        <Field label="Who"><TagPicker people={people} value={f.tags} onChange={(tags) => setF({ ...f, tags })} /></Field>
        <Field label="Note"><textarea className="q-input min-h-[60px] w-full" maxLength={500} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="milestone-note-input" /></Field>
        <Field label={`Photos (${(ms?.photos?.length || 0) + files.length}/20)`}>
          <input type="file" multiple accept="image/*,.heic" className="block min-h-[44px] text-sm text-q-muted" onChange={(e) => setFiles([...e.target.files].slice(0, 20 - (ms?.photos?.length || 0)))} data-testid="milestone-photos-input" />
        </Field>
        <button className="q-btn q-btn-primary min-h-[44px] w-full" disabled={busy} onClick={save} data-testid="milestone-save-btn">{busy ? "Saving…" : "Save milestone"}</button>
      </div>
    </FormDialog>
  );
}

export function Viewer({ hubId, people, items, index, onClose, onChanged }) {
  const [i, setI] = useState(index);
  const [edit, setEdit] = useState(false);
  const [rm, setRm] = useState(false);
  const touch = useRef(null);
  const m = items[i];
  const go = (n) => setI((x) => Math.min(items.length - 1, Math.max(0, x + n)));
  useEffect(() => {
    const on = (e) => { if (e.key === "ArrowRight") go(1); else if (e.key === "ArrowLeft") go(-1); else if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  });
  const remove = () => api.post(`/organizations/${hubId}/memories/${m.id}/remove`).then(() => { toast.success("Removed from Memories. The original is still there."); onChanged(); onClose(); }).catch((e) => toast.error(errMsg(e)));
  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "rgba(0,0,0,0.96)" }} role="dialog" aria-label="Memory viewer" data-testid="memory-viewer"
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }} onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? 0); if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1); }}>
      <div className="flex items-center justify-between p-2">
        <span className="px-2 text-sm text-white/70" data-testid="viewer-position">{i + 1} / {items.length}</span>
        <div className="flex gap-1">
          {m.can_edit && <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/10" aria-label="Edit" onClick={() => setEdit(true)} data-testid="viewer-edit-btn"><Pencil className="h-5 w-5" /></button>}
          {m.can_edit && <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/10" aria-label="Remove from Memories" onClick={() => setRm(true)} data-testid="viewer-remove-btn"><EyeOff className="h-5 w-5" /></button>}
          <button className="inline-flex h-11 w-11 items-center justify-center rounded-full text-white hover:bg-white/10" aria-label="Close" onClick={onClose} data-testid="viewer-close-btn"><X className="h-6 w-6" /></button>
        </div>
      </div>
      <div className="relative flex min-h-0 flex-1 items-center justify-center px-2">
        {m.kind === "video" ? <video key={m.id} src={src(m.video)} poster={src(m.thumb) || undefined} controls playsInline className="max-h-full max-w-full" data-testid="viewer-video" />
          : <img key={m.id} src={src(m.full)} alt={m.caption || "Memory"} className="max-h-full max-w-full object-contain" data-testid="viewer-image" />}
        {i > 0 && <button className="absolute left-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white" aria-label="Previous" onClick={() => go(-1)} data-testid="viewer-prev"><ChevronLeft className="h-6 w-6" /></button>}
        {i < items.length - 1 && <button className="absolute right-2 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-white" aria-label="Next" onClick={() => go(1)} data-testid="viewer-next"><ChevronRight className="h-6 w-6" /></button>}
      </div>
      <div className="p-4 text-white" data-testid="viewer-info">
        {m.caption && <p className="font-semibold" data-testid="viewer-caption">{m.caption}</p>}
        <p className="text-sm text-white/70">{fmtDate(m.date)} · {m.source_label} · {m.author_name}</p>
        {m.tags.length > 0 && <p className="text-sm text-white/70" data-testid="viewer-tags">With {m.tags.map((t) => t.name).join(", ")}</p>}
      </div>
      {edit && <EditDialog hubId={hubId} people={people} m={m} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); onChanged(); onClose(); }} />}
      <Confirm open={rm} onClose={() => setRm(false)} title="Remove from Memories?" body="It's only hidden here. The original photo or message stays." onConfirm={remove} testid="viewer-remove-confirm" />
    </div>
  );
}
